import React from 'react';
import PropTypes from 'prop-types';
import { Card } from 'material-ui/Card';
import { ViewTitle } from 'admin-on-rest';
import LayoutGridField from 'react-jsonschema-form-layout-grid';
import { reduxForm } from 'redux-form';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import get from 'lodash.get';
import uniqBy from 'lodash.uniqby';
import { getFieldSchemaFromField, sanitizeFields } from '../helpers/fields';
import JSONSchemaForm from 'react-jsonschema-form';
import { VALIDATORS } from '../helpers/validators';
import {
  STATUSES,
  STATUSES_POSITIVE,
  STATUSES_CALLED,
  STATUSES_WITH_COMMENTS,
  STATUSES_CALLBACK_SCHEDULE,
  isWithConfirm,
  STATUSES_CALLBACK_NO_SCHEDULE
} from '../helpers/contacts';
import { FORMAT_DATE_TIME } from '../helpers/date';
import EditableFormField from './EditableFormField';
import JSONSchemaInput from './JSONSchemaInput';
import StatusWidget from './StatusWidget';
import StatusDisplayWidget from './StatusDisplayWidget';
import DurationWidget from './DurationWidget';
import DateTimeWidget from './DateTimeWidget';
import CallsWidget from './CallsWidget';
import CallbackDateWidget from './CallbackDateWidget';
import { scrollToVisibleSelector } from '../helpers/dom';

const getErrorKey = error => `${error.name}_${error.property}_${error.message}`;

const transformErrors = errors => {
  const localErrors = errors.filter(
    error =>
      !/(should be|should match exactly one schema in oneOf|should match format "date-time"|should match format "date")/i.test(
        error.message
      )
  );
  return uniqBy(localErrors, getErrorKey);
};

const validateBuilder = fields => {
  return (formData, errors) => {
    Object.keys(formData).forEach(fieldIdentifier => {
      const field = fields.find(field => field.identifier === fieldIdentifier);
      if (field && field.validators) {
        field.validators.forEach(validator => {
          if (VALIDATORS[validator]) {
            const value = formData[fieldIdentifier];
            if (!VALIDATORS[validator](value, formData)) {
              if (errors[fieldIdentifier] && errors[fieldIdentifier].addError) {
                errors[fieldIdentifier].addError('Invalid ' + validator);
              }
            }
          }
        });
      }
    });
    return errors;
  };
};

export class CallScreenForm extends React.Component {
  _getFormPropsForFieldsData = (
    fieldsData = {},
    editable = false,
    showHidden = false
  ) => {
    const {
      fields: originalFields = [],
      columnsNumber = 1,
      sections = []
    } = fieldsData;

    const fields = sanitizeFields(originalFields).filter(
      field => showHidden || field.visible
    );

    const colSize = Math.floor(12 / (columnsNumber || 1));
    let index = 0;
    const sectionedLayoutSchema = sections.map(section => {
      const cols = [];
      let visibleFieldsInSection = 0;
      while (
        visibleFieldsInSection < section.fieldsNumber &&
        fields.length > index
      ) {
        if (fields.length > index && fields[index].identifier) {
          cols.push({
            'ui:col': {
              md: colSize,
              children: [fields[index].identifier]
            }
          });
          if (fields[index].visible) {
            visibleFieldsInSection++;
          }
          index++;
        }
      }
      return {
        'ui:group': section.title,
        'ui:row': cols
      };
    });

    if (index < fields.length) {
      const cols = [];
      for (let i = index; i < fields.length; i++) {
        cols.push({
          'ui:col': {
            md: colSize,
            children: [fields[i].identifier]
          }
        });
      }
      sectionedLayoutSchema.push({
        'ui:group': '',
        'ui:row': cols
      });
    }

    const schema = {
      title: 'Preview form',
      description: 'form',
      type: 'object',
      properties: {
        ...fields.reduce((acc, field) => {
          if (field.visible || editable) {
            acc[field.identifier] = getFieldSchemaFromField(field).schema;
          }
          return acc;
        }, {}),
        callbackDate: {
          title: ' ',
          type: 'string'
        },
        calls: {
          title: ' ',
          type: 'string'
        },
        status: {
          title: ' ',
          type: 'string'
        },
        ...(editable
          ? {}
          : {
              call: {
                title: ' ',
                type: 'object',
                properties: {
                  status: {
                    title: 'Status',
                    type: 'string',
                    enum: STATUSES
                  }
                },
                required: ['status'],
                dependencies: {
                  status: {
                    oneOf: [
                      ...STATUSES.map(status => {
                        let required = [];
                        let extra = {};
                        if (STATUSES_WITH_COMMENTS.indexOf(status) > -1) {
                          extra = {
                            ...extra,
                            subject: {
                              title: 'Subject',
                              type: 'string'
                            },
                            comment: {
                              title: 'Comment',
                              type: 'string'
                            }
                          };
                          // required.push('subject');
                        }
                        if (STATUSES_CALLBACK_SCHEDULE.indexOf(status) > -1) {
                          extra = {
                            ...extra,
                            callbackDate: {
                              title: 'Date to call back',
                              type: 'string',
                              format: 'date-time',
                              dateFormat: FORMAT_DATE_TIME
                            }
                          };
                          if (
                            STATUSES_CALLBACK_NO_SCHEDULE.indexOf(status) ===
                              -1 &&
                            !this.props.isEditContact
                          ) {
                            required.push('callbackDate');
                          }
                        }
                        if (STATUSES_CALLED.indexOf(status) > -1) {
                          extra = {
                            ...extra,
                            start: {
                              title: 'Start time',
                              type: 'string',
                              format: 'date-time',
                              dateFormat: FORMAT_DATE_TIME
                            },
                            duration: {
                              title: 'Call duration time (seconds)',
                              type: 'string'
                              // pattern: '/[0-9]+:[0-6][0-9]:[0-6][0-9]/'
                            }
                          };
                        }

                        return {
                          status: {
                            enum: [status]
                          },
                          details: {
                            title: 'Details',
                            type: 'object',
                            required,
                            properties: extra
                          }
                        };
                      })
                        .filter(obj => Object.keys(obj).length > 0)
                        .map(properties => ({ properties }))
                    ]
                  }
                },
                submit: {
                  type: 'string',
                  title: 'Save'
                }
              }
            })
      },
      required: fields
        .filter(field => field.required)
        .map(field => field.identifier)
    };
    const uiSchema = {
      'ui:order': fields.map(field => field.identifier),
      callbackDate: {
        'ui:widget': CallbackDateWidget
      },
      calls: {
        'ui:widget': CallsWidget
      },
      status: {
        'ui:widget': StatusDisplayWidget
      },
      call: {
        details: {
          duration: {
            'ui:widget': DurationWidget
          },
          start: {
            'ui:widget': DateTimeWidget
          },
          callbackDate: {
            'ui:widget': DateTimeWidget
          }
        },
        status: {
          'ui:field': 'status'
        },
        submit: {
          'ui:widget': props => <button type="submit">Save</button>
        }
      },
      'ui:field': 'layout_grid',
      'ui:layout_grid': {
        'ui:row': [
          {
            'ui:col': {
              md: 12,
              children: sectionedLayoutSchema
            }
          },
          {
            'ui:group': 'Call info',
            'ui:col': {
              md: 12,
              children: ['callbackDate', 'calls', 'status', 'call']
            }
          }
        ]
      },
      ...fields.reduce((acc, field) => {
        if (editable) {
          acc[field.identifier] = {
            'ui:title': ' ',
            'ui:description': ' ',
            'ui:widget': props => {
              const schemaField = getFieldSchemaFromField(field);
              return (
                <EditableFormField
                  {...props}
                  key={field.identifier}
                  identifier={field.identifier}
                  name={field.identifier + '_field'}
                  formData={{ ...field }}
                  onChange={this._updateField}
                  onForward={this._moveFieldForward}
                  onBack={this._moveFieldBack}
                  onDropOnIdentifier={this._moveFieldToIdentifier}
                  editable
                  hidden={!field.visible}
                  component={props => (
                    <JSONSchemaInput
                      {...props}
                      schema={schemaField.schema}
                      uiSchema={schemaField.uiSchema}
                    />
                  )}
                />
              );
            }
          };
        } else {
          acc[field.identifier] = getFieldSchemaFromField(field).uiSchema;
        }
        return acc;
      }, {})
    };
    if (this.props.modifyMode) {
      schema.calls = {};
      uiSchema.calls = {};
    }
    return {
      schema,
      uiSchema,
      fields: {
        layout_grid: LayoutGridField,
        duration: DurationWidget,
        status: StatusWidget
      },
      transformErrors,
      validate: validateBuilder(fields)
    };
  };

  _updateField = (identifier, data) => {
    const updatedFields = get(this.props, 'formSettings.fields', []).map(
      field => {
        if (field.identifier === identifier) {
          return data;
        }
        return field;
      }
    );
    this.props.onFieldChange({
      ...this.props.formData,
      fields: updatedFields
    });
  };

  _moveFieldToPosition = (identifier, position) => {
    return this._moveField(identifier, { position, doSwitch: false });
  };

  _getPositionOfIdentifier = identifier => {
    const fields = get(this.props, 'formSettings.fields', []);
    return fields.findIndex(field => field.identifier === identifier);
  };

  _moveFieldToIdentifier = (droppedIdentifier, targetIdentifier) => {
    return this._moveField(droppedIdentifier, {
      position: this._getPositionOfIdentifier(targetIdentifier),
      doSwitch: false
    });
  };

  _moveField = (
    identifier,
    { moveAmount = 0, position = -1, doSwitch = true }
  ) => {
    const fields = get(this.props, 'formSettings.fields', []);
    const currentPosition = this._getPositionOfIdentifier(identifier);
    let newPosition;
    if (position !== -1) {
      newPosition = position;
    } else {
      newPosition = moveAmount + currentPosition;
    }
    if (fields[newPosition]) {
      if (doSwitch) {
        const fieldToSwichWith = fields[newPosition];
        fields[newPosition] = fields[currentPosition];
        fields[currentPosition] = fieldToSwichWith;
      } else {
        const currentField = fields.splice(currentPosition, 1);
        fields.splice(
          newPosition - (currentPosition < newPosition ? 1 : 0),
          0,
          currentField[0]
        );
      }

      this.props.onFieldChange({
        ...this.props.formData,
        fields: [...fields]
      });
    }
  };

  _moveFieldBack = identifier =>
    this._moveField(identifier, { moveAmount: -1 });
  _moveFieldForward = identifier =>
    this._moveField(identifier, { moveAmount: 1 });

  _onChange = data => {
    console.log('callscreenform onChange', data);
    const status = get(data, 'formData.call.status');
    if (status) {
      const formData = { ...data.formData };
      if (!formData.call) {
        formData.call = {};
      } else {
        formData.call = { ...formData.call };
      }
      if (!formData.call.details) {
        formData.call.details = {
          start: new Date()
        };
      } else {
        formData.call.details = { ...formData.call.details };
      }
      formData.call.details.duration =
        Date.now() - new Date(formData.call.details.start).getTime() + '';
      this.props.onDataChange({ formData });
      if (
        isWithConfirm(status) &&
        // eslint-disable-next-line no-restricted-globals
        confirm('Are you sure you want to continue')
      ) {
        this._onSubmit({});
      }
    }
  };

  _onSubmit = data => {
    if (this.props.onFormSubmit) {
      this.props.onFormSubmit(data);
    }
  };

  _onError = () => {
    scrollToVisibleSelector('.has-error');
    if (this.props.onFormError) {
      this.props.onFormError();
    }
  };

  _onFormRef = form => {
    this.form = form;
  };

  render() {
    return (
      <Card>
        <ViewTitle title="Call Screen" />
        <DndProvider backend={HTML5Backend}>
          <div className="bootstrap " style={{ margin: 20 }}>
            <JSONSchemaForm
              ref={this._onFormRef}
              options={{ addSubmit: false }}
              {...this._getFormPropsForFieldsData(
                this.props.formSettings,
                this.props.editable,
                this.props.showHidden
              )}
              formData={this.props.formData || {}}
              onError={this._onError}
              onChange={this._onChange}
              onSubmit={this._onSubmit}
            >
              {this.props.editable && (
                <div style={{ display: 'none' }}>
                  <button className="btn btn-primary" type="submit">
                    Save
                  </button>
                </div>
              )}
              {!this.props.editable && (
                <button className="btn btn-primary" type="submit">
                  Save
                </button>
              )}
              <p>&nbsp;</p>
            </JSONSchemaForm>
          </div>
        </DndProvider>
      </Card>
    );
  }
}

CallScreenForm.propTypes = {
  editable: PropTypes.bool,
  showHidden: PropTypes.bool,
  formSettings: PropTypes.object.isRequired,
  formData: PropTypes.object,
  onFieldChange: PropTypes.func,
  onDataChange: PropTypes.func,
  onFormSubmit: PropTypes.func,
  modifyMode: PropTypes.bool,
  isEditContact: PropTypes.bool
};

export default reduxForm({ form: 'record-form' })(CallScreenForm);



// WEBPACK FOOTER //
// ./src/components/CallScreenForm.js